import React, { ReactNode } from 'react'
import { makeStyles } from '@mui/styles'
import {
  Grid,
  GridProps,
  Skeleton,
  Typography,
  TypographyProps,
} from '@mui/material'

import {
  CatalogItem,
  Option,
  ProfileAttribute,
  StyledButton,
  TUserAttribute,
  User,
} from 'shared'
import { EditTabContext } from '../EditTab'

import { EditIcon } from 'assets/icons'
import { MultiValue, SingleValue } from 'react-select'
import { UserProfileValues } from './PersonalInformation/helpers'

export type EditTabPropsType = {
  children: (error: boolean) => React.ReactNode
  onClose: () => void
  attributes?: Record<string, TUserAttribute | undefined>
  onSubmit?: (value: UserProfileValues) => void
  isAttributesLoading?: boolean
}

const useStyles = makeStyles((theme) => {
  return {
    contentTitle: {
      fontSize: '18px',
      fontWeight: '600',
    },
    infoBlock: {
      width: '100%',
      padding: '24px',
      backgroundColor: '#F6F8FA',
      borderRadius: theme.shape.borderRadius,

      display: 'flex',
      flexDirection: 'column',
      gap: '12px',

      fontWeight: '400',
      fontSize: '14px',
      color: '#3E4147',
    },
  }
})

export type ProfileEditableBlockType = {
  creator: User
  onEditClick: (context: EditTabContext) => void
}

export type HeaderInfoBlockType = {
  creator: User | null
  showOnProfilePage?: boolean
}

export const EditHeader = ({
  title,
  onEdit,
}: {
  title: string
  onEdit?: () => void
}) => {
  const classes = useStyles()

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      marginBottom="18px"
    >
      <Typography className={classes.contentTitle}>{title}</Typography>
      <StyledButton
        onClick={onEdit}
        style={{ minWidth: 'unset' }}
        startIcon={<EditIcon style={{ marginRight: '4px' }} />}
        variant="outlined"
      >
        <Typography fontWeight={600}>Edit</Typography>
      </StyledButton>
    </Grid>
  )
}

export const KeyValueRow = ({
  rowKey,
  value,
}: {
  rowKey: string
  value: string | undefined
}) => {
  return (
    <Grid container justifyContent="space-between" gap="24px" flexWrap="nowrap">
      <Typography maxWidth="50%">{rowKey}</Typography>
      <Typography maxWidth="50%" fontWeight={500} textAlign="right">
        {value || '-'}
      </Typography>
    </Grid>
  )
}

export const InfoBlockContainer = (
  props: React.ComponentProps<typeof Grid>
) => {
  const classes = useStyles()

  return (
    <Grid
      className={`${classes.infoBlock} ${props.className || ''}`}
      {...props}
    >
      {props.children}
    </Grid>
  )
}

export const InfoRow = ({
  label,
  value,
}: {
  label: ReactNode
  value: ReactNode
}) => {
  return (
    <Grid>
      <Typography
        fontSize="14px"
        color="rgba(3, 16, 29, 0.80)"
        lineHeight={1}
        mb={1}
      >
        {label}
      </Typography>
      <Typography fontSize="16px" fontWeight="500" lineHeight={1}>
        {value || '-'}
      </Typography>
    </Grid>
  )
}

export const ProfileBlockContainer = ({
  children,
  sx,
  ...props
}: GridProps) => {
  return (
    <Grid
      sx={{
        padding: '16px',
        borderRadius: '16px',
        backgroundColor: (theme) => theme.palette.common.white,
        maxWidth: '100%',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Grid>
  )
}

export const ProfileBlockTitle = ({ children, ...props }: TypographyProps) => {
  return (
    <Typography fontSize="20px" fontWeight="600" lineHeight={1.2} {...props}>
      {children}
    </Typography>
  )
}

export const EditTabTitle = (
  props: React.ComponentProps<typeof Typography>
) => {
  return (
    <Typography
      variant="h3"
      marginBottom="24px"
      fontSize="20px"
      fontWeight={600}
      {...props}
    >
      {props.children}
    </Typography>
  )
}

export const TwoColumnedLayout = (props: React.ComponentProps<typeof Grid>) => {
  return (
    <Grid alignItems="center" container gap="16px" flexWrap="nowrap" {...props}>
      {props.children}
    </Grid>
  )
}

export const LabeledSkinCardGroupContainer = ({
  label,
  children,
  isLoading = false,
  skeletonCount = 6,
}: {
  label: string
  isLoading?: boolean
  skeletonCount?: number
  children: React.ReactNode
}) => {
  return (
    <Grid marginBottom="16px">
      <Typography fontSize="14px" marginBottom="4px">
        {label}
      </Typography>
      <Grid container spacing={2}>
        {isLoading
          ? [...Array(skeletonCount || 0).keys()].map((el) => (
              <Grid item md={4} xs={6} key={el}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))
          : children}
      </Grid>
    </Grid>
  )
}

export const SkinCardContainer = (props: React.ComponentProps<typeof Grid>) => {
  return (
    <Grid item md={4} xs={6} {...props}>
      {props.children}
    </Grid>
  )
}

export const onChangeAttribute = (
  attribute: TUserAttribute | undefined,
  selectedOption: SingleValue<Option> | MultiValue<Option>,
  onChange: (newValue: ProfileAttribute | null) => void
) => {
  if (!attribute) return
  let newValue = null
  if (Array.isArray(selectedOption)) {
    newValue = attribute?.options?.filter((item) =>
      selectedOption.find((el) => el.value === item.code)
    )
    if (!newValue?.length) {
      return onChange(null)
    }
  } else {
    newValue = attribute?.options?.find(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      (item) => selectedOption?.value === item.code
    )
    if (!newValue) {
      return onChange(null)
    }
  }

  const catalogItem = {
    type: attribute.id,
    category: attribute.category,
    value: newValue,
  }
  onChange(catalogItem)
}

export const getAttrSelectValue = (
  isMulti: boolean,
  fieldValue: CatalogItem | CatalogItem[]
) => {
  let value: Option | Option[] | null = null

  if (isMulti && Array.isArray(fieldValue)) {
    value = fieldValue.map(({ code, text }) => ({
      label: text,
      value: code,
    }))
  } else if (fieldValue && !Array.isArray(fieldValue)) {
    value = {
      value: fieldValue.code,
      label: fieldValue.text,
    }
  }

  return value
}
